import { Share, Plus, X, Compass, Copy, Check } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

/**
 * Modal di istruzioni per installare la PWA su iPhone.
 *
 * Apple impone webkit a TUTTI i browser iOS, ma SOLO Safari espone
 * "Aggiungi alla schermata Home". Chrome/Firefox/Edge su iPhone NON
 * possono installare PWA. Per questo abbiamo DUE varianti:
 *
 * - `variant="safari"` (default): istruzioni 3-step per installare via
 *   Safari Share menu → Aggiungi a Home.
 * - `variant="open-in-safari"`: l'utente è su Chrome/Firefox/Edge iOS,
 *   non può installare direttamente. Lo guidiamo ad aprire la pagina
 *   in Safari (icona Compass nel menu Condividi del browser corrente).
 *
 * Mostra entrambe le varianti con grafica/icone per essere chiaro a
 * medici BDD non-tecnici.
 */
export default function IosInstallModal({ open, onClose, variant = "safari" }) {
  const [copied, setCopied] = useState(false);
  if (!open) return null;

  const isOpenInSafari = variant === "open-in-safari";
  const currentUrl = typeof window !== "undefined" ? window.location.origin + "/" : "";

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback per browser senza clipboard API: seleziona testo
      // (raro su iOS moderno)
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="ios-install-modal-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="ios-install-modal"
      >
        {/* Header viola con icona */}
        <div className="bg-gradient-to-br from-[#1a1fa8] to-[#3B42DD] px-6 pt-6 pb-5 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition-colors"
            data-testid="ios-install-modal-close"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-bold pr-8">
            {isOpenInSafari ? "Apri questa pagina in Safari" : "Installa l'app sul tuo iPhone"}
          </h2>
          <p className="text-sm text-white/85 mt-1">
            {isOpenInSafari
              ? "Su iPhone solo Safari può installare l'app. Segui questi passi."
              : "Avrai l'icona BDD sulla schermata Home, come una vera app."}
          </p>
        </div>

        {/* Step-by-step — due varianti */}
        {isOpenInSafari ? (
          <OpenInSafariSteps url={currentUrl} copied={copied} onCopy={copyUrl} />
        ) : (
          <SafariInstallSteps />
        )}

        <div className="px-6 pb-5">
          <Button
            type="button"
            onClick={onClose}
            data-testid="ios-install-modal-ok"
            className="w-full h-11 bg-[#1a1fa8] hover:bg-[#3B42DD] text-white rounded-xl font-semibold"
          >
            Ho capito
          </Button>
          {!isOpenInSafari && (
            <p className="text-[11px] text-slate-400 text-center mt-3 leading-relaxed">
              Funziona solo con <strong>Safari</strong> — se sei su Chrome o
              altri browser su iPhone, apri questa pagina con Safari.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// Steps per Safari iOS: Aggiungi a Home
function SafariInstallSteps() {
  return (
    <div className="px-6 py-5 space-y-4">
      <Step
        num="1"
        icon={<Share className="w-5 h-5 text-[#1a1fa8]" />}
        title="Tocca il pulsante Condividi"
        desc="Lo trovi in basso al centro della barra di Safari (quadrato con freccia rivolta verso l'alto)."
      />
      <Step
        num="2"
        icon={<Plus className="w-5 h-5 text-[#1a1fa8]" />}
        title="Scorri e tocca «Aggiungi alla schermata Home»"
        desc="Se non lo vedi, scorri verso il basso nel menu."
      />
      <Step
        num="3"
        icon={<span className="text-sm font-bold text-[#1a1fa8]">OK</span>}
        title="Tocca «Aggiungi» in alto a destra"
        desc="L'icona di BDD apparirà sulla tua schermata Home."
      />
    </div>
  );
}

// Steps per Chrome/Firefox/Edge iOS: apri in Safari poi installa
function OpenInSafariSteps({ url, copied, onCopy }) {
  return (
    <div className="px-6 py-5 space-y-4">
      <Step
        num="1"
        icon={<Share className="w-5 h-5 text-[#1a1fa8]" />}
        title="Tocca il pulsante Condividi del browser"
        desc="Su Chrome iOS è in basso, su Firefox/Edge è in alto o nel menu ⋯"
      />
      <Step
        num="2"
        icon={<Compass className="w-5 h-5 text-[#1a1fa8]" />}
        title="Scegli «Apri in Safari»"
        desc="Se non vedi l'opzione, copia il link qui sotto e incollalo nella barra di Safari."
      />
      {/* Riquadro copia-link */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase font-semibold text-slate-500 mb-0.5">
            Link da incollare in Safari
          </div>
          <div className="text-[12px] font-mono text-slate-700 truncate" title={url}>
            {url}
          </div>
        </div>
        <button
          type="button"
          onClick={onCopy}
          data-testid="ios-install-copy-url"
          className={`flex-shrink-0 inline-flex items-center gap-1 px-2.5 h-8 rounded-md text-xs font-semibold transition-colors ${
            copied ? "bg-emerald-500 text-white" : "bg-[#1a1fa8] text-white hover:bg-[#3B42DD]"
          }`}
          aria-label="Copia link"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copiato" : "Copia"}
        </button>
      </div>
      <Step
        num="3"
        icon={<Plus className="w-5 h-5 text-[#1a1fa8]" />}
        title="Una volta in Safari, segui i 3 passi standard"
        desc="Tocca Condividi → Aggiungi alla schermata Home → Aggiungi."
      />
    </div>
  );
}

function Step({ num, icon, title, desc }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#1a1fa8]/10 flex items-center justify-center font-bold text-[#1a1fa8] text-sm">
        {num}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-slate-100 flex-shrink-0">
            {icon}
          </span>
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        </div>
        <p className="text-[12px] text-slate-500 leading-snug">{desc}</p>
      </div>
    </div>
  );
}
